// «Copiar imagen» / «Guardar imagen como…» on a picture in a message or in the viewer (tester
// report 2026-10-14: an image could not be copied to paste into Mattermost, Word, WhatsApp Web…;
// every client gained it at once — docs/PARITY.md).
//
// The browser's own image menu never shows over a message: MessageRow takes the right-click for the
// message menu, so these items ride at the top of OUR menu when the pointer is on a picture.
//
// What reaches the clipboard is the FULL-RESOLUTION ORIGINAL (`/api/v4/files/{id}`), not the preview
// the tile draws. Browsers only accept `image/png` for images in the async clipboard, so anything else
// (JPEG, GIF → its first frame, WebP, SVG) is re-encoded as PNG through a canvas first.
//
// The write is started SYNCHRONOUSLY inside the click with a PROMISE for the bytes: Safari refuses a
// clipboard write that starts after an await (the user activation is gone), and Chrome takes the
// promise just the same. `copyImageWith` is the testable core; `copyImage` wires the browser.
import { mediaUrls } from '../../media';
import { localUrl } from './localFiles';
import { t } from '../../../i18n/i18n';

export interface ImageRef {
  fileId: string;
  name: string;
  mimeType: string;
}

export type CopyOutcome = 'copied' | 'unsupported' | 'failed';

export interface CopyDeps {
  /** The async clipboard with image support (`navigator.clipboard.write` + `ClipboardItem`). */
  supported: boolean;
  /** The original's bytes. */
  fetchOriginal(): Promise<Blob>;
  /** Any image blob → a PNG blob of the same picture (first frame of an animation). */
  toPng(blob: Blob): Promise<Blob>;
  /** `navigator.clipboard.write([new ClipboardItem(items)])`. */
  write(items: Record<string, Promise<Blob>>): Promise<void>;
}

/** Whether a blob must be re-encoded before the clipboard takes it (only PNG goes in as is). */
export function needsPng(type: string): boolean {
  return type.trim().toLowerCase() !== 'image/png';
}

/**
 * Copies the original: the clipboard write starts BEFORE anything is awaited (see the header), with
 * a promise that fetches and, when needed, converts. Every way it can end is an outcome the caller
 * says out loud — a copy that silently did nothing is the bug being fixed.
 */
export function copyImageWith(deps: CopyDeps): Promise<CopyOutcome> {
  if (!deps.supported) return Promise.resolve('unsupported');
  const png = deps.fetchOriginal().then((blob) => {
    if (!blob.size) throw new Error('empty image');
    return needsPng(blob.type) ? deps.toPng(blob) : blob;
  });
  // A rejected bytes promise also rejects the write; never leave it unhandled on its own.
  png.catch(() => undefined);
  let writing: Promise<void>;
  try {
    writing = deps.write({ 'image/png': png });
  } catch {
    return Promise.resolve('failed');
  }
  return writing.then(
    () => 'copied' as const,
    () => 'failed' as const,
  );
}

/** The original's URL: this tab's own copy when it sent the picture, else the server's file. */
export function originalUrl(fileId: string): string {
  return localUrl(fileId) ?? mediaUrls.file(fileId);
}

function clipboardSupportsImages(): boolean {
  return typeof ClipboardItem !== 'undefined' && typeof navigator !== 'undefined' && !!navigator.clipboard?.write;
}

async function fetchBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
}

/** Re-encodes through a canvas. `createImageBitmap` first; SVG (which it refuses) through an <img>. */
async function blobToPng(blob: Blob): Promise<Blob> {
  let source: CanvasImageSource & { width: number; height: number };
  let release = () => {};
  try {
    const bitmap = await createImageBitmap(blob);
    source = bitmap;
    release = () => bitmap.close();
  } catch {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    try {
      await img.decode();
    } finally {
      URL.revokeObjectURL(url);
    }
    source = img;
    if (!img.naturalWidth) throw new Error('undecodable image');
    img.width = img.naturalWidth;
    img.height = img.naturalHeight;
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height;
    const g = canvas.getContext('2d');
    if (!g) throw new Error('no canvas');
    g.drawImage(source, 0, 0);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('png encode failed'))), 'image/png'),
    );
  } finally {
    release();
  }
}

/** «Copiar imagen» in the browser. Call it from the click itself (user activation). */
export function copyImage(image: ImageRef): Promise<CopyOutcome> {
  return copyImageWith({
    supported: clipboardSupportsImages(),
    fetchOriginal: () => fetchBlob(originalUrl(image.fileId)),
    toPng: blobToPng,
    write: (items) => navigator.clipboard.write([new ClipboardItem(items)]),
  });
}

/** «Guardar imagen como…»: the browser's own download of the original (it asks where when set to). */
export function saveImageAs(image: ImageRef): void {
  const a = document.createElement('a');
  a.href = localUrl(image.fileId) ?? mediaUrls.file(image.fileId, true);
  a.download = image.name || image.fileId;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** The picture under a right-click inside a message (the tiles PostFiles draws), if any. */
export function imageUnder(target: EventTarget | null): ImageRef | null {
  const el = (target as Element | null)?.closest?.('[data-lb-file]') as HTMLElement | null;
  const fileId = el?.dataset.lbFile;
  if (!el || !fileId) return null;
  return { fileId, name: el.dataset.lbName ?? '', mimeType: el.dataset.lbMime ?? '' };
}

/** Copy + the notices every surface says (progress, then the result). */
export function copyImageAndSay(image: ImageRef, say: (message: string, tone?: 'info' | 'error') => void): Promise<CopyOutcome> {
  const outcome = copyImage(image);
  say(t('web.image.copying'));
  return outcome.then((r) => {
    if (r === 'copied') say(t('web.image.copied'));
    else say(t(r === 'unsupported' ? 'web.image.copyUnsupported' : 'web.image.copyFailed'), 'error');
    return r;
  });
}
